import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { InputField } from "@/components/InputField/InputField";
import { Toggle } from "@/components/Toggle/Toggle";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import {
	useCreateDynamicsDealMutation,
	useGetDynamicsDealConfigQuery,
	useGetDynamicsDealCreationTriggerQuery,
	useGetDynamicsOwnersQuery,
	useGetDynamicsPipelinesQuery,
	useGetDynamicsUserMappingsQuery,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { DynamicsPipelinePicker } from "./DynamicsPipelinePicker";
import { resolveSelectableStageId } from "./dynamicsPipeline.utils";

interface ProspectInfo {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	title?: string | null;
	organizationName?: string | null;
}

const DEFAULT_TEMPLATE = "{firstName} {lastName} - {company}";

const buildDealName = (template: string | null | undefined, prospect: ProspectInfo): string =>
	(template || DEFAULT_TEMPLATE)
		.replace("{firstName}", prospect.firstName || "")
		.replace("{lastName}", prospect.lastName || "")
		.replace("{company}", prospect.organizationName || "")
		.replace("{email}", prospect.email || "")
		.replace("{title}", prospect.title || "")
		.trim();

/** `estimatedclosedate` is a Dataverse DateTime, so the date input's value is sent as ISO 8601. */
const toIsoTimestamp = (date: string): string | undefined => {
	if (!date) {
		return undefined;
	}

	const parsed = new Date(`${date}T00:00:00.000Z`);

	return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

interface CreateDynamicsDealModalProps {
	isOpen: boolean;
	onClose: () => void;
	prospectId: number;
	prospect?: ProspectInfo;
}

export const CreateDynamicsDealModal: FunctionComponent<CreateDynamicsDealModalProps> = ({
	isOpen,
	onClose,
	prospectId,
	prospect,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.createDeal.${key}`);

	const { user } = useAppSelector(authSelector);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: dealConfig } = useGetDynamicsDealConfigQuery();
	const { data: pipelinesData } = useGetDynamicsPipelinesQuery();
	const { data: ownersData } = useGetDynamicsOwnersQuery();
	const { data: userMappingsData } = useGetDynamicsUserMappingsQuery();
	const { data: dealCreationTrigger } = useGetDynamicsDealCreationTriggerQuery();
	const [createDeal, { isLoading: isCreating }] = useCreateDynamicsDealMutation();

	const [createAsLead, setCreateAsLead] = useState(false);
	const [dealName, setDealName] = useState("");
	const [pipeline, setPipeline] = useState<string | null>(null);
	const [dealStage, setDealStage] = useState<string | null>(null);
	const [dealOwnerId, setDealOwnerId] = useState<string | null>(null);
	const [closeDate, setCloseDate] = useState("");

	const pipelines = useMemo(() => pipelinesData?.pipelines ?? [], [pipelinesData]);

	useEffect(() => {
		if (typeof dealCreationTrigger?.createAsLead === "boolean") {
			setCreateAsLead(dealCreationTrigger.createAsLead);
		}
	}, [dealCreationTrigger]);

	useEffect(() => {
		if (!dealConfig) {
			return;
		}

		setPipeline(dealConfig.defaultPipelineId ?? null);
		setDealStage(dealConfig.defaultStageId ?? null);

		if (dealConfig.defaultOwnerId) {
			setDealOwnerId(dealConfig.defaultOwnerId);
		}

		setDealName(
			prospect
				? buildDealName(dealConfig.dealNameTemplate, prospect)
				: (dealConfig.dealNameTemplate ?? "")
		);

		if (dealConfig.expectedCloseDateOffset) {
			const date = new Date();

			date.setDate(date.getDate() + dealConfig.expectedCloseDateOffset);
			setCloseDate(date.toISOString().split("T")[0]);
		}
	}, [dealConfig, prospect]);

	useEffect(() => {
		if (userMappingsData && user?.id) {
			const mapping = userMappingsData.find((item) => item.userId === user.id);

			if (mapping) {
				setDealOwnerId(mapping.msdynamicsOwnerId);
			}
		}
	}, [userMappingsData, user?.id]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const handleSubmit = async () => {
		if (!prospectId) {
			return;
		}

		// A stage from the other id space would be dropped by the sync — don't send one.
		const stageId = resolveSelectableStageId(pipelines, pipeline, dealStage);

		try {
			const result = await createDeal({
				prospectId,
				createAsLead,
				dealName: dealName.trim() || undefined,
				pipeline: createAsLead ? undefined : pipeline || undefined,
				dealStage: createAsLead ? undefined : stageId || undefined,
				dealOwnerId: dealOwnerId || undefined,
				closeDate: toIsoTimestamp(closeDate),
			}).unwrap();

			handleOpenSnackbar(
				result.crmDeal.url ? `${ts("success")} — ${result.crmDeal.url}` : ts("success")
			);
			onClose();
		} catch {
			handleOpenSnackbar(ts("error"));
		}
	};

	return (
		<Modal
			disableSave={!prospectId}
			handleClose={onClose}
			handleSave={handleSubmit}
			isLoading={isCreating}
			isOpened={isOpen}
			size="sm"
			submitButtonText={createAsLead ? ts("submitLead") : ts("submitDeal")}
			title={createAsLead ? ts("titleLead") : ts("titleDeal")}
		>
			{renderMessageSnackbar()}
			<div className="grid grid-cols-1 gap-4">
				<div className="flex flex-col items-start gap-3">
					<span className="text-sm font-semibold text-text-meta">{ts("createAsLead")}</span>
					<div className="flex flex-row items-center gap-2">
						<Toggle
							isChecked={createAsLead}
							name="createAsLead"
							onChange={() => setCreateAsLead((prev) => !prev)}
						/>
						<span className="text-xs text-text-meta">{ts("createAsLeadHelp")}</span>
					</div>
				</div>

				<InputField
					handleChange={(event) => setDealName(event.target.value)}
					label={createAsLead ? ts("leadTopic") : ts("dealName")}
					name="dealName"
					placeholder={createAsLead ? ts("leadTopic") : ts("dealName")}
					value={dealName}
				/>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					{!createAsLead && (
						<DynamicsPipelinePicker
							pipelineId={pipeline}
							pipelines={pipelines}
							stageId={dealStage}
							ts={ts}
							onChange={({ pipelineId, stageId }) => {
								setPipeline(pipelineId);
								setDealStage(stageId);
							}}
						/>
					)}

					<Dropdown
						data={ownerOptions}
						defaultValue={ownerOptions.find((option) => option.id === dealOwnerId)}
						floating={true}
						handleSelect={(value) => setDealOwnerId(value.id as string)}
						isOptional={true}
						label={ts("owner")}
					/>

					<InputField
						handleChange={(event) => setCloseDate(event.target.value)}
						isOptional={true}
						label={ts("closeDate")}
						name="closeDate"
						type="date"
						value={closeDate}
					/>
				</div>
			</div>
		</Modal>
	);
};
